import * as React from 'react'

import { CircleCheckIcon, SearchIcon, XIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { AutoSizer, List } from 'react-virtualized'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import StruxhubTextValueSelector from '~/client/src/shared/components/StruxhubInputs/StruxhubSelector/StruxhubTextValueSelector'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'

export interface IFormIconDropdownOption {
  desc: string
  icons: { name: string; desc: string }[]
}

interface IProps {
  selectedValue: string
  targetClassName?: string

  options: IFormIconDropdownOption[]

  onItemClick(iconId: string): void
}

// A 40px tile plus its 5px margins.
const ICON_BOX_PX = 50
// What a group row keeps for its label, above the icon lines.
const GROUP_LABEL_PX = 24
// The row's own left padding, which the icons do not get to use.
const GROUP_PADDING_PX = 16
const MODAL_HEIGHT_RATIO = 0.4
const OVERSCAN_ROW_COUNT = 10

@observer
export default class FormIconDropdown extends React.Component<IProps> {
  @observable private searchValue: string = ''
  @observable private isOpen: boolean = false

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const { selectedValue, targetClassName, options } = this.props

    const allIcons = options.flatMap(o => o.icons)
    const selectedValueName = allIcons.find(o => o.name === selectedValue)?.desc

    return (
      <Popover open={this.isOpen} onOpenChange={this.setOpen}>
        <PopoverTrigger
          nativeButton={false}
          render={
            <span
              className={classList({
                'tw:inline-block tw:grow-0 tw:align-middle': true,
                [targetClassName]: !!targetClassName,
              })}
            />
          }
        >
          <StruxhubTextValueSelector
            className="tw:overflow-hidden tw:flex-none"
            label={Localization.translator.icon}
            isHelperTextCondensed
            value={selectedValueName}
            customIconElement={
              <WorkflowTypeIcon iconName={selectedValue} size="small" />
            }
          />
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          className="tw:w-(--anchor-width) tw:overflow-hidden tw:p-0"
        >
          {this.optionsModal}
        </PopoverContent>
      </Popover>
    )
  }

  private get optionsModal(): JSX.Element {
    const { options, selectedValue } = this.props

    const filteredOptions = options
      .flatMap(group => group.icons)
      .filter(icon =>
        icon.name.toLowerCase().includes(this.searchValue.toLowerCase()),
      )

    return (
      <div className="tw:flex tw:flex-col no-outline-container">
        <div className="virtualized-list-smart-wrapper">
          <StruxhubInput
            className="tw:m-2.5"
            placeholder={Localization.translator.search}
            isRequiredTextHidden
            isRequired={false}
            onChange={this.onSearchChange}
            value={this.searchValue}
            autoFocus
            hideBottomLabel={true}
            customRightIcon={
              <Button
                variant="ghost"
                size="icon-xs"
                className="tw:flex-none! tw:text-muted-foreground"
                aria-label={Localization.translator.clear}
                onMouseDown={this.clearSearch}
                onClick={this.clearSearch}
              >
                <XIcon />
              </Button>
            }
          />
          {this.searchValue ? (
            filteredOptions.length > 0 ? (
              <div className="tw:flex tw:items-center tw:p-2.5 tw:flex-wrap">
                {filteredOptions.map(i => this.renderIcon(i))}
              </div>
            ) : (
              <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:p-2.5">
                <SearchIcon className="tw:mb-[5px] tw:size-5" />
                <div className="tw:flex tw:items-center tw:text-body-md tw:text-foreground">
                  {Localization.translator.noIconsFoundByYourRequest}
                </div>
              </div>
            )
          ) : (
            <AutoSizer disableHeight>
              {({ width }) => {
                // The icon row wraps, so a group is as tall as the lines it needs. Held at one
                // line, a group with more icons than fit pushed the rest out past the popover's
                // edge — present in the DOM, unreachable on screen.
                const perLine = Math.max(
                  1,
                  Math.floor((width - GROUP_PADDING_PX) / ICON_BOX_PX),
                )
                const rowHeight = ({ index }: { index: number }) =>
                  GROUP_LABEL_PX +
                  Math.ceil(options[index].icons.length / perLine) * ICON_BOX_PX

                return (
                  <List
                    // Remount when the wrap changes: List caches row heights by index and
                    // does not recompute them for a new rowHeight function on its own.
                    key={perLine}
                    data={options}
                    width={width}
                    height={Math.min(
                      window.innerHeight * MODAL_HEIGHT_RATIO,
                      options.reduce(
                        (total, _, index) => total + rowHeight({ index }),
                        0,
                      ),
                    )}
                    rowHeight={rowHeight}
                    rowCount={options.length}
                    rowRenderer={this.renderRow}
                    scrollToAlignment="start"
                    scrollToIndex={options.findIndex(o =>
                      o.icons.some(i => i.name === selectedValue),
                    )}
                    overscanRowCount={OVERSCAN_ROW_COUNT}
                  />
                )
              }}
            </AutoSizer>
          )}
        </div>
      </div>
    )
  }

  private renderRow = ({ key, style, index }: any): JSX.Element => {
    const option = this.props.options[index]

    if (!option) {
      return null
    }

    return (
      <div
        key={key}
        style={style}
        className="tw:flex tw:items-start tw:mb-[5px]"
        onClick={() => this.setOpen(false)}
      >
        <div className="tw:pl-4 tw:text-body-sm tw:text-muted-foreground tw:flex tw:flex-col tw:uppercase tw:flex-1">
          {option.desc}
          <div className="tw:flex tw:flex-wrap tw:items-center tw:[&>*]:flex-none">
            {option.icons.map(i => this.renderIcon(i))}
          </div>
        </div>
      </div>
    )
  }

  private renderIcon(i: { name: string; desc: string }): JSX.Element {
    const { selectedValue, onItemClick } = this.props
    return (
      <Tooltip key={i.name}>
        <TooltipTrigger
          render={<div />}
          className={classList({
            'tw:flex tw:items-center tw:[&>*]:flex-none tw:hover:bg-muted! tw:p-2 tw:rounded-[5px] tw:m-[5px] tw:relative tw:min-w-[40px] tw:max-w-[40px] tw:min-h-[40px] tw:max-h-[40px]': true,
            'tw:border tw:border-solid tw:border-foreground':
              selectedValue === i.name,
          })}
          onClick={onItemClick.bind(null, i.name)}
        >
          <WorkflowTypeIcon
            iconName={i.name}
            className="tw:text-body-sm tw:text-foreground"
          />
          {selectedValue === i.name && (
            <span className="tw:absolute tw:-top-[7px] tw:-right-[7px] tw:w-[19px] tw:h-[19px] tw:p-0.5 tw:rounded-full tw:bg-white tw:inline-block tw:pointer-events-none">
              <CircleCheckIcon className="tw:size-[15px] tw:fill-primary tw:text-primary-foreground" />
            </span>
          )}
        </TooltipTrigger>
        <TooltipContent side="top">{i.desc}</TooltipContent>
      </Tooltip>
    )
  }

  @action.bound
  private onSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    this.searchValue = event.target.value
  }

  @action.bound
  private setOpen(isOpen: boolean) {
    this.isOpen = isOpen
  }

  @action.bound
  private clearSearch() {
    this.searchValue = ''
  }
}
